import classNames from 'classnames';
import { PopoverWrapper, PopoverWrapperProps } from '@components/indicator/Popover';
import { TooltipContent, TooltipContentProps } from './TooltipContent';
import './Tooltip.scss';

export interface TooltipProps
  extends Omit<TooltipContentProps, 'children'>,
    Omit<PopoverWrapperProps, 'popoverContent'> {
  text: string;
}

/**
 * Tooltip component - a styled `PopoverWrapper` that uses `TooltipContent` to wrap the `popoverContent`
 * it accepts the same props as `PopoverWrapper` and uses `TooltipContent` props for size and styling the content and arrow.
 * Arrow color is set from tooltip bg tokens per variant (--tooltip-arrow from tooltip.bg.primary/secondary); no arrowColor prop.
 * @param children - The anchor element of the popover. Must be a React.ReactElement.
 * @param className - Additional classes to apply to the tooltip
 * @param text - The text content to display in the popover, will be wrapped inside a `TooltipContent`.
 * @param size - The size of the TooltipContent text. Default large
 * @param variant - Color theme of the content. Default primary
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  className,
  text,
  size = 'large',
  variant = 'primary',
  ...rest
}) => {
  // arrowColor deprecated (PSDS-284), stripped from rest so it is not passed to PopoverWrapper
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructure to omit from rest
  const { arrowColor: _arrowColor, ...popoverRest } = rest;
  const content = (
    <TooltipContent size={size} variant={variant}>
      {text}
    </TooltipContent>
  );

  return (
    <PopoverWrapper
      className={classNames('vds-tooltip', `vds-tooltip--${variant}`, className)}
      popoverContent={content}
      arrowPadding={4}
      offset={16}
      {...popoverRest}>
      {children}
    </PopoverWrapper>
  );
};
